import { fetchBulkOrderByQuery } from '@/api';
import { ALLOW_FILE_CSV } from '@/config';
import { message, notification, Upload } from 'antd';
import { createElement } from 'react';
import { RcFile } from 'antd/es/upload';
import { t } from 'i18next';
import { handleErrorMessage } from '../handleError';

/**
 * Checks if a file has any validation errors for upload.
 * This function is designed to work with Ant Design's Upload component.
 *
 * @param {RcFile} file - The file to check for validation errors. Must be an instance of Ant Design's RcFile.
 *
 * @returns {string | false}
 * - Returns `false` if no validation errors are found (file is valid)
 * - Returns `Upload.LIST_IGNORE` if validation errors are found (file is invalid)
 *
 * @throws {void} This function doesn't throw errors, but displays error messages using Ant Design's message component
 */
export const checkFileValidationError = (file: RcFile) => {
  const isCSVOrXLSX = ALLOW_FILE_CSV.includes(file.type);
  const isFileSizeValid = file.size <= 1048576;

  if (!isCSVOrXLSX) {
    message.error(t('only_csv_xlsx'));
    return Upload.LIST_IGNORE;
  }
  if (!isFileSizeValid) {
    message.error(t('file_size_limit_exceeded'));
    return Upload.LIST_IGNORE;
  }

  return false;
};

export const downloadFile = async (
  uid: string,
  fetchFunction: (uid: string) => Promise<Blob>,
  fileName: string,
  /** Sees the parsed error body first; returning true means handled, no error toast. */
  onError?: (error: any) => boolean,
) => {
  try {
    const fileBlob = await fetchFunction(uid);

    const url = window.URL.createObjectURL(fileBlob);

    triggerDownload(url, fileName);

    return true;
  } catch (_error: any) {
    const error = await convertBlobErrorToJSON(_error);

    if (onError?.(error)) return false;

    handleErrorMessage(error, t('operation_failed_please_try_again'), true);

    return false;
  }
};

export const triggerDownload = (url: string, fileName: string) => {
  // Owner 10-06: browsers ignore `download` on another origin (signed B2
  // links), so the file opened in place of the dashboard. A cross-origin file
  // opens in a new tab instead; core signs it as an attachment, so it saves.
  const crossOrigin =
    url &&
    !url.startsWith('blob:') &&
    new URL(url, window.location.href).origin !== window.location.origin;
  if (crossOrigin) {
    // BOXY-647: polled files (labels, exports) are ready seconds after the click,
    // outside the user gesture, so the popup blocker silently ate the new tab.
    // Blocked → offer a link the user clicks.
    const tab = window.open(url, '_blank');
    if (tab) {
      tab.opener = null;
      return;
    }
    notification.info({
      key: url,
      message: t('file_ready'),
      description: createElement(
        'a',
        {
          href: url,
          target: '_blank',
          rel: 'noopener noreferrer',
          onClick: () => notification.destroy(url),
        },
        t('open_file'),
      ),
      duration: 0,
    });
    return;
  }

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);

  document.body.appendChild(link);
  link.click();

  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const convertBlobErrorToJSON = async (error: any) => {
  if (error.response && error.response.data instanceof Blob) {
    const errorText = await error.response.data.text();
    const errorJSON = JSON.parse(errorText);

    return { ...errorJSON, statusCode: error.status };
  } else {
    console.error('Error:', error);
    return null;
  }
};

export const exportOrders = async (queryString: string, fileName?: string) => {
  return await downloadFile(
    queryString,
    fetchBulkOrderByQuery,
    `orders-${new Date().getTime()}.csv`, // Timestamp only for unique filename, not data
  );
};
